import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

internal data class TourManualPlacement(val entryId: String, val lane: Int, val position: Int)

@Composable
internal fun TourManualDrawDialog(slot: TourSlotDto, entries: List<TourEntryDto>, placements: List<TourDrawEntryDto>,
    busy: Boolean, canSave: Boolean, error: String?, onClose: () -> Unit, onSave: (List<TourManualPlacement>) -> Unit) {
    val players = remember(entries) { entries.sortedWith(compareBy(tourNameComparator) { it.playerName }) }
    var seats by remember { mutableStateOf(entries.associate { entry ->
        val place = placements.firstOrNull { it.entryId == entry.id }
        entry.id to ((place?.lane?.toString().orEmpty()) to (place?.position?.toString().orEmpty()))
    }) }
    val rows = players.mapNotNull { entry ->
        val seat = seats[entry.id]
        val lane = seat?.first?.toIntOrNull(); val position = seat?.second?.toIntOrNull()
        if (lane in 1..8 && position in 1..3) TourManualPlacement(entry.id, lane!!, position!!) else null
    }
    val duplicateSeats = rows.groupBy { it.lane to it.position }.filterValues { it.size > 1 }.keys
    val duplicateEntries = rows.filter { (it.lane to it.position) in duplicateSeats }.map { it.entryId }.toSet()
    val complete = rows.size == players.size && players.isNotEmpty()
    val currentBusy by rememberUpdatedState(busy)
    val close by rememberUpdatedState(onClose)
    DisposableEffect(Unit) {
        val previous = document.activeElement as? HTMLElement
        val overflow = document.body?.style?.getPropertyValue("overflow").orEmpty()
        document.body?.style?.setProperty("overflow", "hidden")
        val listener: (Event) -> Unit = { event ->
            val key = event as? KeyboardEvent
            if (key?.key == "Escape") { key.preventDefault(); if (!currentBusy) close() }
            if (key?.key == "Tab") {
                val fields = document.getElementById("tour-manual-draw-dialog")?.querySelectorAll("select:not(:disabled),button:not(:disabled)")
                val first = fields?.item(0) as? HTMLElement
                val last = fields?.item((fields.length - 1).coerceAtLeast(0)) as? HTMLElement
                if (first == null) key.preventDefault()
                else if (key.shiftKey && document.activeElement == first) { key.preventDefault(); last?.focus() }
                else if (!key.shiftKey && document.activeElement == last) { key.preventDefault(); first.focus() }
            }
        }
        window.addEventListener("keydown", listener)
        onDispose {
            window.removeEventListener("keydown", listener)
            document.body?.style?.setProperty("overflow", overflow)
            if (previous?.isConnected == true) previous.focus()
        }
    }
    LaunchedEffect(Unit) { (document.querySelector("#tour-manual-draw-dialog select") as? HTMLElement)?.focus() }
    Div(attrs = { classes("modal-backdrop"); onClick { if (!busy) onClose() } }) {
        Div(attrs = {
            classes("match-modal"); id("tour-manual-draw-dialog")
            attr("role", "dialog"); attr("aria-modal", "true"); attr("aria-labelledby", "tour-manual-draw-title")
            onClick { it.stopPropagation() }
        }) {
            H2(attrs = { id("tour-manual-draw-title") }) { Text("Ruční los · ${slot.roundNo}. runda") }
            P { Text("Každému přihlášenému hráči zvolte dráhu a pořadí 1–3. Los lze upravit pouze před zahájením rundy.") }
            TourError(error)
            Form(attrs = { onSubmit { it.preventDefault(); if (!busy && canSave && complete && duplicateSeats.isEmpty()) onSave(rows) } }) {
                Div(attrs = { classes("table-scroll") }) {
                    Table(attrs = { classes("tour-admin-table"); attr("aria-label", "Ruční rozlosování") }) {
                        Thead { Tr { Th { Text("Hráč") }; Th { Text("Dráha") }; Th { Text("Pořadí na dráze") } } }
                        Tbody { players.forEach { entry ->
                            val seat = seats[entry.id] ?: ("" to "")
                            val duplicate = entry.id in duplicateEntries
                            Tr(attrs = { if (duplicate) classes("tour-draw-duplicate") }) {
                                Td(attrs = { classes("tour-player-name") }) { Text(entry.playerName) }
                                Td { Select(attrs = {
                                    attr("aria-label", "Dráha: ${entry.playerName}"); if (busy) disabled()
                                    if (duplicate) { attr("aria-invalid", "true"); attr("aria-describedby", "tour-manual-draw-conflict") }
                                    onChange { seats = seats + (entry.id to (it.value.orEmpty() to seats[entry.id]?.second.orEmpty())) }
                                }) { (listOf("" to "Vyberte") + (1..8).map { it.toString() to it.toString() }).forEach { (value, label) ->
                                    Option(value, attrs = { if (value == seat.first) selected() }) { Text(label) }
                                } } }
                                Td { Select(attrs = {
                                    attr("aria-label", "Pořadí: ${entry.playerName}"); if (busy) disabled()
                                    if (duplicate) { attr("aria-invalid", "true"); attr("aria-describedby", "tour-manual-draw-conflict") }
                                    onChange { seats = seats + (entry.id to (seats[entry.id]?.first.orEmpty() to it.value.orEmpty())) }
                                }) { (listOf("" to "Vyberte") + (1..3).map { it.toString() to it.toString() }).forEach { (value, label) ->
                                    Option(value, attrs = { if (value == seat.second) selected() }) { Text(label) }
                                } } }
                            }
                        } }
                    }
                }
                if (duplicateSeats.isNotEmpty()) P(attrs = { classes("editor-error"); id("tour-manual-draw-conflict"); attr("role", "alert") }) { Text("Na jednom místě nemohou být dva hráči. Opravte duplicitní dráhu a pořadí.") }
                else if (!complete) P(attrs = { classes("muted") }) { Text("Přiřazeno ${rows.size} z ${players.size} hráčů.") }
                Div(attrs = { classes("tour-actions") }) {
                    Button(attrs = { classes("primary"); type(ButtonType.Submit); if (busy || !canSave || !complete || duplicateSeats.isNotEmpty()) disabled() }) { Text(if (busy) "Ukládám…" else "Uložit los") }
                    Button(attrs = { type(ButtonType.Button); if (busy) disabled(); onClick { onClose() } }) { Text("Zrušit") }
                }
            }
        }
    }
}
